import { useCallback, useRef } from 'react';
import useFormDraft from './useFormDraft';

const cloneFormData = (value) => {
  if (value == null || typeof value !== 'object') return value;
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value));
  }
};

/**
 * Standard create-form draft wiring: snapshot + merge apply + submit props.
 */
export default function useFormDraftBindings({
  formKey,
  userId,
  isEditMode = false,
  allowEditDraft = false,
  enabled = true,
  formData,
  setFormData,
  mergeDefaults = null,
  pickSnapshot = null,
  mergeSnapshot = null,
  getDefaultFormData = null,
  onDiscardDraft = null,
}) {
  const initialFormRef = useRef(null);
  if (!isEditMode && initialFormRef.current === null && formData != null) {
    initialFormRef.current = cloneFormData(formData);
  }

  const applyDraft = useCallback(
    (data) => {
      setFormData((prev) => {
        if (mergeSnapshot) return mergeSnapshot(prev, data);
        return mergeDefaults ? { ...mergeDefaults, ...prev, ...data } : { ...prev, ...data };
      });
    },
    [setFormData, mergeDefaults, mergeSnapshot],
  );
  const getDraftSnapshot = useCallback(
    () => (pickSnapshot ? pickSnapshot(formData) : formData),
    [formData, pickSnapshot],
  );

  const resetForm = useCallback(() => {
    if (allowEditDraft && onDiscardDraft) {
      onDiscardDraft();
      return;
    }
    if (getDefaultFormData) {
      setFormData(getDefaultFormData());
    } else if (!isEditMode && initialFormRef.current != null) {
      setFormData(cloneFormData(initialFormRef.current));
    }
  }, [allowEditDraft, onDiscardDraft, getDefaultFormData, isEditMode, setFormData]);

  const formDraft = useFormDraft({
    formKey,
    userId,
    isEditMode,
    allowEditDraft,
    enabled,
    getSnapshot: getDraftSnapshot,
    applySnapshot: applyDraft,
    resetForm,
  });

  const submitDraftProps = {
    showSaveDraft: formDraft.canSaveDraft,
    onSaveDraft: formDraft.saveDraft,
    draftSavedAt: formDraft.lastSavedAt,
  };

  return { formDraft, submitDraftProps };
}
